import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-luxon';
import { getFormatedLabels } from './helper/getFormatedLabels';
import { useEffect, useMemo, memo } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  registerables,
  TimeScale,
} from 'chart.js';
import { ChartData } from './types';

const customCrosshair = {
  id: 'customCrosshair',
  afterEvent(chart: any, args: any) {
    const { event } = args;
    if (event.type === 'mouseout') {
      chart._crosshairX = null;
      chart.draw();
      return;
    }
    if (event.type === 'mousemove') {
      const { left, right } = chart.chartArea;
      if (event.x >= left && event.x <= right) {
        chart._crosshairX = event.x;
      } else {
        chart._crosshairX = null;
      }
      chart.draw();
    }
  },
  afterDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const crosshairX = chart._crosshairX;
    if (crosshairX == null) return;

    const {
      ctx,
      chartArea: { top, bottom },
    } = chart;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(crosshairX, top);
    ctx.lineTo(crosshairX, bottom);
    ctx.strokeStyle = pluginOptions.color || '#919EAB';
    ctx.lineWidth = pluginOptions.width || 1;
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(
  ...registerables,
  LineElement,
  CategoryScale,
  TimeScale,
  LinearScale,
  customCrosshair,
  zoomPlugin
);

const chartPlugins = [zoomPlugin];

type LineChartProps = {
  chartRef: any;
  options: any;
  allSeries: any;
  extendedColors: string[];
};

const LineChart = memo(({ chartRef, options, allSeries, extendedColors }: LineChartProps) => {
  const formattedLabels = useMemo(() => getFormatedLabels({ data: allSeries }), [allSeries]);

  const datasets = useMemo(
    () =>
      allSeries.map((item: ChartData, index: number) => {
        const yAxisID = index === 0 ? 'y' : `y${index - 1}`;
        const colorIndex = index;

        const color = extendedColors[colorIndex];

        return {
          label: item.name,
          data: item.data.map(([timestamp, value]) => ({
            x: new Date(timestamp),
            y: value,
          })),
          yAxisID,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 2,
          radius: 0,
          pointHoverRadius: 0,
        };
      }),
    [allSeries, extendedColors]
  );
  const userData = useMemo(
    () => ({
      labels: formattedLabels,
      datasets,
    }),
    [formattedLabels, datasets]
  );

  useEffect(() => {
    if (chartRef && chartRef.current) {
      chartRef.current.canvas.style.touchAction = 'unset';
    }
  }, [chartRef]);

  return (
    <Line
      ref={chartRef as React.MutableRefObject<any>}
      data={userData}
      options={options}
      plugins={chartPlugins}
      style={{ maxHeight: '400px', height: '400px' }}
    />
  );
});

export default LineChart;
